import { computed, defineComponent, onMounted, watch, type FunctionalComponent } from 'vue'
import { useMemoAsync } from '@/utils/async/useMemoAsync'
import { FindexClient } from '@/integrations/findex'
import { useUserSettings } from '@/modules/settings/hooks/useUserSettings'
import { prettyNumber } from '@/utils/number/format'
import { useForexTickers } from '@/integrations/findex/hooks/forex/useForexTickers'
import { twColor } from '@/utils/style'
import { useUserWorth } from '@/modules/user/hooks/useUserWorth'
import { investmentsRefreshCount } from '~/composables/useDataRefresh'
import EmptyState from 'components/empty-state'
import Button from 'components/button'
import { pushAddAssetModal } from '@/modules/asset/modals/add-asset'

// @ts-ignore
import IconHome from 'components/icons/IconHome.vue'
import { EAssetType } from 'findef'
import { Spinner } from 'components/spinner'
import { useResponsive } from '~/composables/useResponsive'
import { ValueCard, type ValueCardSection } from '@/modules/dashboard/components/value-card'
import { DiversificationWidget } from '@/modules/dashboard/widgets/diversification-widget'
import { NetWorthWidget } from '@/modules/dashboard/widgets/networth-widget'
import { NewsWidget } from '@/modules/dashboard/widgets/news-widget'
import { PortfolioValueWidget } from '@/modules/dashboard/widgets/portfolio-value-widget'
import HomeIcon from 'assets/img/svg/icons/home.svg?url'

export default defineComponent(() => {
  definePageMeta({
    layout: 'app',
    name: 'Dashboard',
    icon: {
      src: HomeIcon
    }
  })
  const responsive = useResponsive();
  const settings = useUserSettings()
  const forex = useForexTickers()
  const userWorth = useUserWorth()
  const diversification = computed(() => userWorth.worth.value.diversification || []);



  const EmptyStateAction: FunctionalComponent = () => {
    return (
      <Button
        icon={{
          src: async () => import('assets/img/svg/icons/plus.svg?raw'),
          stroke: 'white',
          fill: 'currentColor',
          size: '0.8rem'
        }}
        onClick={() =>
          pushAddAssetModal({
            onSave: async () => {
              await userWorth.refresh()
              await investmentCountQuery.refresh()
              await liabilityCountQuery.refresh()
              await totalInvestedQuery.refresh()
            }
          })
        }
      >
        Add Asset
      </Button>
    )
  }

  onMounted(() => {
    userWorth.refresh().catch((e) => console.error(e))
  })

  watch(investmentsRefreshCount, () => {
    userWorth.refresh().catch((e) => console.error(e))
  })

  const liabilityCountQuery = useMemoAsync(async () => {
    const [data] = await FindexClient.getLiabilitiesCount()
    return data?.count || 0
  }, [investmentsRefreshCount])

  const investmentCountQuery = useMemoAsync(async () => {
    const [data] = await FindexClient.getInvestmentsCount()
    return data?.count || 0
  }, [investmentsRefreshCount])

  const totalInvestedQuery = useMemoAsync(async () => {
    const [data] = await FindexClient.getTotalInvested()
    return data || { value: 0, type: settings.currency.value }
  }, [investmentsRefreshCount])

  const totalLiquidCashQuery = useMemoAsync(async () => {
    const [data] = await FindexClient.getTotalLiquidCash()
    return data || { value: 0, type: settings.currency.value }
  }, [investmentsRefreshCount]);


  const diversificationCashSlot = computed(() => diversification.value.find(it => it.assetType === EAssetType.CASH));
  const cashCurrencyFrom = computed(() => diversificationCashSlot.value?.value?.type || settings.currency.value);
  const cashTicker = computed(
    () => forex.getTickerRef(cashCurrencyFrom.value, settings.currency.value).value
  )
  const diversificationCashValue = computed(() => {
    const cash = diversificationCashSlot.value;
    if (!cash) return { value: totalLiquidCashQuery.data?.value ?? 0, type: settings.currency.value };
    return { value: cash.value.value * cashTicker.value.bid, type: settings.currency.value };
  })

  const snapshotCountQuery = useMemoAsync(async () => {
    const [data] = await FindexClient.getSnapshotCount();
    return data?.count ?? 0;
  }, [investmentsRefreshCount]);

  const snapshotCount = computed(() => snapshotCountQuery.data ?? 0);

  const isLoading = computed(
    () =>
      liabilityCountQuery.loading ||
      investmentCountQuery.loading ||
      totalInvestedQuery.loading ||
      totalLiquidCashQuery.loading ||
      userWorth.isLoading.value ||
      snapshotCountQuery.loading ||
      false
  )

  const portfolioIsEmpty = computed(() => {
    if (isLoading.value) return false
    return userWorth.worth.value.assetCount <= 0 && userWorth.worth.value.liabilityCount <= 0 && snapshotCount.value <= 0
  })

  const liabilityCountSection = computed((): ValueCardSection => {
    return {
      title: 'Liabilities',
      value: liabilityCountQuery.data || 0,
      loading: liabilityCountQuery.loading
    }
  })

  const assetCountSection = computed((): ValueCardSection => {
    return {
      title: 'Assets',
      value: investmentCountQuery.data || 0,
      loading: investmentCountQuery.loading
    }
  })

  const totalInvestedSection = computed((): ValueCardSection => {
    return {
      title: 'Total Invested',
      value: prettyNumber(
        (totalInvestedQuery.data?.value || 0) *
        forex.getTickerRef(
          totalInvestedQuery.data?.type || settings.currency.value,
          settings.currency.value
        ).value.bid,
        {
          american: true,
          minDecimals: 0,
          maxDecimals: 0,
          suffix: settings.currency.value
        }
      ),
      loading: totalInvestedQuery.loading
    }
  })

  const totalLiquidCashSection = computed((): ValueCardSection => {
    return {
      title: 'Liquid Cash',
      value: prettyNumber(
        diversificationCashValue.value.value,
        {
          american: true,
          minDecimals: 0,
          maxDecimals: 0,
          suffix: settings.currency.value
        }
      ),
      loading: totalLiquidCashQuery.loading
    }
  })


  return () => {
    if (isLoading.value) {
      return (
        <div class="w-full h-full flex items-center justify-center">
          <Spinner />
        </div>
      )
    }

    if (portfolioIsEmpty.value) {
      return (
        <EmptyState
          title="Nothing to show yet"
          subtitle="Add your first asset to get your dashboard going!"
          icon={() => <IconHome />}
          action={EmptyStateAction}
        />
      )
    }

    const valueCards = computed(() => {
      return [
        <ValueCard
          iconBackground={twColor('primary-100')}
          icon={{
            src: async () => import('assets/img/svg/icons/briefcase.svg?raw'),
            fill: twColor('primary-500'),
            size: '1.45rem'
          }}
          sections={[assetCountSection, liabilityCountSection]}
        />,
        <ValueCard
          iconBackground={twColor('primary-100')}
          icon={{
            src: async () => import('assets/img/svg/icons/wallet2.svg?raw'),
            fill: twColor('primary-500'),
            size: '1.5rem'
          }}
          sections={[totalInvestedSection]}
        />,
        <ValueCard
          iconBackground={twColor('primary-100')}
          icon={{
            src: async () => import('assets/img/svg/icons/money_wave.svg?raw'),
            fill: twColor('primary-500'),
            size: '1.65rem'
          }}
          sections={[totalLiquidCashSection]}
        />
      ]
      //   return <div class="col-start-2 grid grid-rows-[1fr_1fr_1fr] gap-4">

      //       </div>
    })

    if (!isLoading.value) {
      if (responsive.isSmall.value) {
        return <div class="w-full grid grid-auto-rows gap-4">
          <PortfolioValueWidget class="w-full" />
          <div class="w-full grid grid-auto-rows gap-4">
            {valueCards.value}
          </div>
          <div class="min-h-[283px]">
            <NewsWidget />
          </div>
          <div class="min-h-[439px]">
            <NetWorthWidget class="min-h-[439px]" />
          </div>
          <div class="min-h-[537px]">
            <DiversificationWidget />
          </div>
        </div>
      }

      return (
        <div class="grid grid-cols-[minmax(max(36%,320px),1fr)_1fr_36%] grid-auto-rows gap-4 select-none">
          <div class="grid grid-cols-subgrid col-span-2">
            <PortfolioValueWidget />
            <div class="col-start-2 grid grid-rows-[1fr_1fr_1fr] gap-4">
              {valueCards.value}
            </div>
          </div>
          <NewsWidget />
          <NetWorthWidget class="col-span-2" />
          <DiversificationWidget />
        </div>
      )
    }
  }
})
